import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Search, Filter, ArrowDownUp, RefreshCw, List, Eye, Pill, AlertTriangle, X, Package, IndianRupee, Calendar, AlertCircle } from "lucide-react";
import axiosInstance from "../../../src/api/axiosInstance";
import Authenticated from "../../layout/Authenticated";

const Stock = () => {
    const [stockData, setStockData] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit] = useState(20);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [lowStockFilter, setLowStockFilter] = useState("");
    const [sortField, setSortField] = useState("name");
    const [sortDirection, setSortDirection] = useState("asc");
    const isInitialMount = useRef(true);
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewingId, setViewingId] = useState(null);
    const [showFilterBox, setShowFilterBox] = useState(false);

    // Fetch stock data
    const fetchStock = async (searchQuery = "", lowStockQuery = "") => {
        setLoading(true);
        try {
            let url = `/stock?page=${page}&limit=${limit}`;

            if (searchQuery.trim()) {
                url += `&search=${encodeURIComponent(searchQuery.trim())}`;
            }
            if (lowStockQuery.trim()) {
                url += `&low_stock=${encodeURIComponent(lowStockQuery.trim())}`;
            }

            const response = await axiosInstance.get(url);
            setStockData(response.data.stock || response.data); // Adjust based on API response structure
            setTotal(response.data.total || response.data.length);
            setError("");
        } catch (err) {
            console.error("Error fetching stock:", err);
            setError("Failed to load stock data. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    // Reset page to 1 when search/filter terms change
    useEffect(() => {
        if (!isInitialMount.current && page !== 1) {
            setPage(1);
        }
    }, [searchTerm, lowStockFilter]);

    // Fetch stock when page or filters change
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchStock(searchTerm, lowStockFilter);
            if (isInitialMount.current) {
                isInitialMount.current = false;
            }
        }, (!isInitialMount.current && (searchTerm || lowStockFilter)) ? 500 : 0);

        return () => clearTimeout(timeoutId);
    }, [page, searchTerm, lowStockFilter]);

    // Sorting function
    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDirection("asc");
        }
    };

    const handleClearFilters = () => {
        setSearchTerm("");
        setLowStockFilter("");
        setPage(1);
    };

    // Client-side filtering if needed (fallback)
    const filteredStock = stockData.filter((item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
        (lowStockFilter === "" || (lowStockFilter === "yes" && item.total_tablets < 22)) // Example threshold for low stock
    );

    // Sort stock
    const sortedStock = [...filteredStock].sort((a, b) => {
        if (!sortField) return 0;

        let comparison = 0;

        if (sortField === "name") {
            comparison = a.name.localeCompare(b.name);
        } else if (sortField === "total_strips") {
            comparison = parseFloat(a.total_strips) - parseFloat(b.total_strips);
        } else if (sortField === "total_tablets") {
            comparison = a.total_tablets - b.total_tablets;
        }

        return sortDirection === "asc" ? comparison : -comparison;
    });

    // Function to get sort indicator
    const getSortIndicator = (field) => {
        if (sortField !== field) return null;
        return sortDirection === "asc" ? "↑" : "↓";
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString();
    };

    // Check if low stock (example: total_tablets < 100)
    const isLowStock = (item) => item.total_tablets < 100;

    const handleViewClick = (medicineId) => {
        setViewingId(medicineId);
        setShowViewModal(true);
    };

    const handleCloseModal = () => {
        setShowViewModal(false);
        setViewingId(null);
    };

    // Inline modal component for viewing stock details
    const StockModal = ({ id, onClose }) => {
        const [medicine, setMedicine] = useState(null);
        const [loadingModal, setLoadingModal] = useState(true);
        const [modalError, setModalError] = useState("");

        useEffect(() => {
            const fetchMedicineStock = async () => {
                if (!id) return;
                setLoadingModal(true);
                try {
                    // Assuming API endpoint for single medicine stock details
                    const res = await axiosInstance.get(`/stock/${id}`);
                    setMedicine(res.data);
                    setModalError("");
                } catch (err) {
                    console.error("Error fetching stock details:", err);
                    setModalError("Failed to load stock details.");
                } finally {
                    setLoadingModal(false);
                }
            };

            fetchMedicineStock();
        }, [id]);

        if (loadingModal) {
            return (
                <div className="p-6">
                    <div className="flex items-center gap-4 mb-6">
                        <div className="h-10 w-10 bg-gray-100 rounded-full animate-pulse"></div>
                        <div className="flex-1 h-4 bg-gray-100 rounded animate-pulse"></div>
                    </div>
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                        <div className="h-64 animate-pulse bg-gray-50 rounded"></div>
                    </div>
                </div>
            );
        }

        if (modalError || !medicine) {
            return (
                <div className="p-6 text-center">
                    <p className="text-red-600">{modalError || "No data available."}</p>
                    <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-500 text-white rounded">Close</button>
                </div>
            );
        }

        return (
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
                <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-gray-200">
                    <div className="bg-teal-600 p-4 rounded-t-2xl relative flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-white/20 rounded-xl">
                                <Pill className="h-6 w-6 text-white" />
                            </div>

                            <div>
                                <h1 className="text-lg font-semibold text-white">
                                    Stock: {medicine.name}
                                </h1>
                                <p className="text-xs text-teal-100">
                                    Batch-wise Stock Details
                                </p>
                            </div>
                        </div>

                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-white/20 rounded-full transition"
                        >
                            <X className="h-5 w-5 text-white" />
                        </button>
                    </div>

                    {/* BODY */}
                    <div className="p-5 space-y-6">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 shadow-sm">
                                <p className="text-xs font-semibold text-teal-700 mb-1">Total Strips</p>
                                <p className="text-2xl font-bold text-teal-800">
                                    {parseFloat(medicine.total_strips).toFixed(2)}
                                </p>
                            </div>

                            {/* Total Tablets */}
                            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 shadow-sm">
                                <p className="text-xs font-semibold text-blue-700 mb-1">Total Tablets</p>
                                <p className="text-2xl font-bold text-blue-800">
                                    {medicine.total_tablets}
                                </p>
                            </div>

                            {/* Status */}
                            <div
                                className={`rounded-xl p-4 border shadow-sm
        ${isLowStock(medicine)
                                        ? "bg-red-50 border-red-200"
                                        : "bg-emerald-50 border-emerald-200"
                                    }`}
                            >
                                <p
                                    className={`text-xs font-semibold mb-1
            ${isLowStock(medicine) ? "text-red-700" : "text-emerald-700"}
        `}
                                >
                                    Stock Status
                                </p>

                                <p
                                    className={`text-xl font-bold 
            ${isLowStock(medicine) ? "text-red-700" : "text-emerald-700"}
        `}
                                >
                                    {isLowStock(medicine) ? "Low Stock" : "In Stock"}
                                </p>
                            </div>
                        </div>

                        {/* TABLE SECTION */}
                        <div>
                            <h2 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                <Package className="h-5 w-5 text-teal-600" />
                                Batch-wise Stock
                            </h2>

                            <div className="overflow-x-auto border rounded-xl shadow-sm">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-gray-100 text-gray-700 text-xs uppercase">
                                            <th className="px-4 py-3 text-left font-semibold">Batch No.</th>
                                            <th className="px-4 py-3 text-left font-semibold">Available</th>
                                            <th className="px-4 py-3 text-left font-semibold">Expiry</th>
                                            <th className="px-4 py-3 text-right font-semibold">Price</th>
                                        </tr>
                                    </thead>

                                    <tbody className="divide-y divide-gray-200">
                                        {(medicine.batches || []).map((batch, idx) => {
                                            const isExpiringSoon = batch.expiry_date &&
                                                new Date(batch.expiry_date) < new Date(Date.now() + 90 * 24 * 60 * 60 * 1000);

                                            return (
                                                <tr key={idx} className="hover:bg-gray-50">
                                                    <td className="px-4 py-3">
                                                        <span className="text-sm font-medium text-gray-800">
                                                            {batch.batch_number || 'N/A'}
                                                        </span>
                                                    </td>

                                                    <td className="px-4 py-3">
                                                        <span className={`font-semibold 
                                                ${batch.available_units < 10 ? "text-red-600" : "text-gray-800"}`}>
                                                            {batch.available_units || 0}
                                                        </span>
                                                    </td>

                                                    <td className="px-4 py-3 flex items-center gap-2">
                                                        <Calendar className="h-4 w-4 text-gray-500" />
                                                        <span className={`text-sm 
                                                ${isExpiringSoon ? "text-orange-600" : "text-gray-700"}`}
                                                        >
                                                            {formatDate(batch.expiry_date)}
                                                        </span>
                                                    </td>

                                                    <td className="px-4 py-3 text-right">
                                                        <span className="font-semibold text-emerald-700 flex justify-end items-center gap-1">
                                                            <IndianRupee className="h-4 w-4" />
                                                            {Number(batch.selling_price_per_unit).toFixed(2)}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Empty */}
                            {(medicine.batches || []).length === 0 && (
                                <div className="text-center py-8 bg-gray-50 border border-dashed rounded-xl mt-3">
                                    <Package className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                                    <p className="text-gray-600 text-sm">No batch details found</p>
                                </div>
                            )}
                        </div>

                        {/* CLOSE BUTTON */}
                        <div className="flex justify-end">
                            <button
                                onClick={onClose}
                                className="px-6 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div >
        );
    };

    return (
        <Authenticated>
            <div className="min-h-screen bg-gray-50 py-6 px-2 sm:px-4 md:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="mb-8">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex items-center space-x-3">
                                <div className="bg-teal-600 p-3 rounded-xl shadow-md">
                                    <Package className="h-8 w-8 text-white" />
                                </div>
                                <div>
                                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Stock Management</h1>
                                    <p className="text-xs sm:text-sm text-gray-600 mt-1">View and manage medicine stock inventory</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Search and Filters */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
                        <div className="flex flex-col gap-4">
                            {/* Search Row */}
                            <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between">
                                <div className="relative flex-1 w-full">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                    <input
                                        type="text"
                                        className="pl-10 w-full py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-200 focus:border-teal-600 transition-all"
                                        placeholder="Search by medicine name..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                                <div className="flex gap-2 w-full md:w-auto">
                                    <button
                                        className="flex items-center gap-2 px-4 py-2.5 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-all"
                                        onClick={() => fetchStock(searchTerm, lowStockFilter)}
                                    >
                                        <RefreshCw className="w-4 h-4" />
                                        <span className="hidden md:inline">Refresh</span>
                                    </button>
                                    <button
                                        className="flex items-center gap-2 px-4 py-2.5 text-white bg-teal-600 border border-gray-300 rounded-lg hover:bg-teal-700 transition-all"
                                        onClick={() => setShowFilterBox(!showFilterBox)}
                                    >
                                        <Filter className="w-4 h-4 text-white" />
                                        <span className="hidden md:inline">Filter</span>
                                    </button>

                                </div>
                            </div>

                            {/* Filter Row */}
                            <div className="flex flex-col sm:flex-row gap-4 items-center">
                                {showFilterBox && (
                                    <div className="flex-1 w-full">
                                        <select
                                            className="w-full py-2.5 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-200 focus:border-teal-600 transition-all"
                                            value={lowStockFilter}
                                            onChange={(e) => setLowStockFilter(e.target.value)}
                                        >
                                            <option value="">All Stock</option>
                                            <option value="yes">Low Stock Only</option>
                                        </select>
                                    </div>
                                )}
                                <div className="flex items-center text-sm text-gray-600">
                                    {searchTerm || lowStockFilter ? (
                                        <span className="font-medium">
                                            Showing filtered results: <strong>{total}</strong> items
                                        </span>
                                    ) : (
                                        <span className="font-medium">
                                            Total: <strong>{total}</strong> items
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl flex items-center gap-2">
                            <AlertCircle size={20} />
                            <span className="font-medium">{error}</span>
                        </div>
                    )}

                    {/* Content Section */}
                    {loading ? (
                        <div className="flex justify-center items-center py-16">
                            <RefreshCw className="w-10 h-10 text-teal-600 animate-spin" />
                        </div>
                    ) : sortedStock.length === 0 ? (
                        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-10 text-center">
                            <h3 className="text-xl font-bold text-gray-900 mb-2">No stock found</h3>
                            <p className="text-gray-600">There are no stock items matching your criteria.</p>
                        </div>
                    ) : (
                        // ⬇️ GRID VIEW REMOVED — Only table view will render
                        <div className="bg-white rounded-2xl shadow-lg border border-teal-100 overflow-x-auto hover:shadow-xl transition-shadow duration-300">
                            <div className="overflow-x-auto">
                                <table className="w-full table-auto min-w-max text-xs sm:text-sm">
                                    <thead>
                                        <tr className="bg-gradient-to-r from-teal-600 to-teal-700 text-white">
                                            <th
                                                className="text-left py-4 px-6 font-semibold uppercase tracking-wider cursor-pointer hover:bg-teal-700 transition-colors"
                                                onClick={() => handleSort("name")}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <span>Medicine Name</span>
                                                    {sortField === "name" && (
                                                        <ArrowDownUp className="h-4 w-4" />
                                                    )}
                                                </div>
                                            </th>
                                            <th
                                                className="text-left py-4 px-6 font-semibold uppercase tracking-wider cursor-pointer hover:bg-teal-700 transition-colors"
                                                onClick={() => handleSort("total_strips")}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <span>Total Strips</span>
                                                    {sortField === "total_strips" && (
                                                        <ArrowDownUp className="h-4 w-4" />
                                                    )}
                                                </div>
                                            </th>
                                            <th
                                                className="text-left py-4 px-6 font-semibold uppercase tracking-wider cursor-pointer hover:bg-teal-700 transition-colors"
                                                onClick={() => handleSort("total_tablets")}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <span>Total Tablets</span>
                                                    {sortField === "total_tablets" && (
                                                        <ArrowDownUp className="h-4 w-4" />
                                                    )}
                                                </div>
                                            </th>
                                            <th className="text-left py-4 px-6 font-semibold uppercase tracking-wider">
                                                Status
                                            </th>
                                            <th className="text-left py-4 px-6 font-semibold text-white uppercase tracking-wider">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody className="divide-y divide-gray-200">
                                        {sortedStock.map((item) => (
                                            <tr
                                                key={item.medicine_id}
                                                className="hover:bg-teal-50 transition-all duration-200"
                                            >
                                                <td className="py-4 px-6 text-gray-900 font-medium">
                                                    {item.name}
                                                </td>
                                                <td className="py-4 px-6 text-gray-700">
                                                    {parseFloat(item.total_strips).toFixed(2)}
                                                </td>
                                                <td className="py-4 px-6 text-gray-700">
                                                    {item.total_tablets}
                                                </td>
                                                <td className="py-4 px-6">
                                                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${isLowStock(item)
                                                        ? "bg-red-100 text-red-700"
                                                        : "bg-green-100 text-green-700"
                                                        }`}>
                                                        {isLowStock(item) ? "Low Stock" : "In Stock"}
                                                    </span>
                                                </td>
                                                <td className="py-4 px-6">
                                                    <button
                                                        onClick={() => handleViewClick(item.medicine_id)}
                                                        className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-all"
                                                    >
                                                        <Eye size={20} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {sortedStock.length > 0 && (
                                <div className="px-6 py-4 bg-gray-50 text-sm text-gray-600 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-3">
                                    <div className="font-medium">
                                        Showing {sortedStock.length} of {total} items
                                    </div>

                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setPage(page - 1)}
                                            disabled={page === 1}
                                            className={`px-4 py-2 rounded-lg transition-all ${page === 1
                                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                                : "bg-teal-600 text-white hover:bg-teal-700"
                                                }`}
                                        >
                                            Previous
                                        </button>

                                        <button
                                            onClick={() => setPage(page + 1)}
                                            disabled={page * limit >= total}
                                            className={`px-4 py-2 rounded-lg transition-all ${page * limit >= total
                                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                                : "bg-teal-600 text-white hover:bg-teal-700"
                                                }`}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* View Modal */}
            {showViewModal && viewingId && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-auto shadow-lg">
                        <div className="p-4">
                            <StockModal id={viewingId} onClose={handleCloseModal} />
                        </div>
                    </div>
                </div>
            )}
        </Authenticated>
    );
};

export default Stock;